import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type {
  CCHIGapItem,
  CCHIGapScanResponse,
  MedicalInsuranceException,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const REASON_CODES = [
  "provider_renewal_gap",
  "pending_dependent_documents",
  "employee_opted_out_with_spouse_coverage",
  "short_term_assignment_covered_abroad",
  "other_documented",
] as const;

const STATUS_TABS = ["active", "expired", "revoked"] as const;

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function GrantExceptionDialog({
  legalEntityId,
  prefillEmployeeId,
  onClose,
  onGranted,
}: {
  legalEntityId: string;
  prefillEmployeeId?: string;
  onClose: () => void;
  onGranted: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: prefillEmployeeId ?? "",
    reason_code: REASON_CODES[0] as string,
    reason_detail: "",
    effective_from: todayIsoDate(),
    effective_until: plusDaysIso(60),
    granted_by_clerk_user_id: "hr_director",
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.grantMedicalInsuranceException({
        legal_entity_id: legalEntityId,
        ...form,
      }),
    onSuccess: () => {
      onGranted();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t(
        "compliance.cchi_grant_title",
        "Grant CCHI medical exception",
      )}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!form.employee_id || !form.reason_detail.trim()}
            className="rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("compliance.cchi_grant_submit", "Grant exception")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {t(
              "compliance.cchi_warning",
              "CCHI mandate is mode-invariant — no autonomy mode bypasses. This grant is audit-logged in the cryptographic chain.",
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("compliance.cchi_employee_id", "Employee ID (UUID)")}
          </label>
          <input
            value={form.employee_id}
            onChange={(e) =>
              setForm({ ...form, employee_id: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("compliance.cchi_reason_code", "Reason")}
          </label>
          <select
            value={form.reason_code}
            onChange={(e) =>
              setForm({ ...form, reason_code: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          >
            {REASON_CODES.map((c) => (
              <option key={c} value={c}>
                {c.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("compliance.cchi_reason_detail", "Reason detail")}
          </label>
          <textarea
            value={form.reason_detail}
            onChange={(e) =>
              setForm({ ...form, reason_detail: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            rows={3}
            placeholder={t(
              "compliance.cchi_reason_placeholder",
              "Be specific — the audit chain captures this verbatim.",
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("compliance.cchi_effective_from", "Effective from")}
            </label>
            <input
              type="date"
              value={form.effective_from}
              onChange={(e) =>
                setForm({ ...form, effective_from: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("compliance.cchi_effective_until", "Effective until")}
            </label>
            <input
              type="date"
              value={form.effective_until}
              onChange={(e) =>
                setForm({ ...form, effective_until: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
        </div>
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function GapItemRow({
  item,
  onGrant,
}: {
  item: CCHIGapItem;
  onGrant: () => void;
}) {
  const { t } = useTranslation();
  const tone =
    item.days_since_hire > 30
      ? "rose"
      : item.days_since_hire > 7
      ? "amber"
      : "slate";
  const borderClass =
    tone === "rose"
      ? "border-rose-200 bg-rose-50/40"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50/40"
      : "border-border-subtle bg-white";
  return (
    <div className={`flex items-center justify-between gap-3 rounded-2xl border p-3 ${borderClass}`}>
      <div className="flex items-center gap-3">
        <ShieldAlert
          className={`h-5 w-5 ${
            tone === "rose"
              ? "text-rose-600"
              : tone === "amber"
              ? "text-amber-600"
              : "text-text-secondary"
          }`}
        />
        <div>
          <div className="text-sm font-medium text-text-primary">
            {item.employee_name_en ?? item.employee_id.slice(0, 8)}
          </div>
          <div className="text-xs text-text-tertiary">
            {item.employee_code ?? item.employee_id.slice(0, 8)} ·{" "}
            {t("compliance.cchi_hired", "Hired")} {item.hire_date} ·{" "}
            {item.days_since_hire}d
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onGrant}
        className="rounded-lg border border-brand-deep px-3 py-1.5 text-xs font-medium text-brand-deep hover:bg-surface-sunken"
      >
        {t("compliance.cchi_grant_exception", "Grant exception")}
      </button>
    </div>
  );
}

function ExceptionRow({
  exc,
  onRevoke,
}: {
  exc: MedicalInsuranceException;
  onRevoke: (id: string) => void;
}) {
  const { t } = useTranslation();
  const statusIcon =
    exc.status === "active"
      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      : exc.status === "expired"
      ? <CalendarClock className="h-4 w-4 text-amber-600" />
      : <XCircle className="h-4 w-4 text-rose-600" />;
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {statusIcon}
          <span className="text-sm font-medium text-text-primary">
            {exc.employee_id.slice(0, 8)}…
          </span>
          <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-[10px] uppercase tracking-wide text-text-secondary">
            {exc.status}
          </span>
        </div>
        {exc.status === "active" && (
          <button
            type="button"
            onClick={() => onRevoke(exc.id)}
            className="rounded-lg border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
          >
            {t("compliance.cchi_revoke", "Revoke")}
          </button>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("compliance.cchi_reason_code", "Reason")}
          </dt>
          <dd>{exc.reason_code.replace(/_/g, " ")}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("compliance.cchi_window", "Window")}
          </dt>
          <dd>
            {exc.effective_from} → {exc.effective_until}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-text-tertiary">
            {t("compliance.cchi_detail", "Detail")}
          </dt>
          <dd className="text-text-primary">{exc.reason_detail}</dd>
        </div>
        {exc.revoke_reason && (
          <div className="col-span-2">
            <dt className="text-text-tertiary">
              {t("compliance.cchi_revoke_reason", "Revoke reason")}
            </dt>
            <dd className="text-rose-700">{exc.revoke_reason}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export function BenefitsCCHIPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusTab, setStatusTab] =
    useState<(typeof STATUS_TABS)[number]>("active");
  const [grantOpen, setGrantOpen] = useState(false);
  const [prefillEmpId, setPrefillEmpId] = useState<string | undefined>();

  const scan = useQuery<CCHIGapScanResponse>({
    queryKey: ["cchi-scan", legalEntityId],
    queryFn: () => api.scanCCHIGaps(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const exceptions = useQuery<MedicalInsuranceException[]>({
    queryKey: ["cchi-exceptions", legalEntityId, statusTab],
    queryFn: () =>
      api.listMedicalInsuranceExceptions(legalEntityId ?? "", {
        status: statusTab,
      }),
    enabled: !!legalEntityId,
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.revokeMedicalInsuranceException(id, {
        revoked_by_clerk_user_id: "hr_director",
        revoke_reason: "HR-initiated revoke from CCHI dashboard",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cchi-exceptions"] });
      qc.invalidateQueries({ queryKey: ["cchi-scan"] });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("compliance.cchi_title", "CCHI medical-insurance compliance")}
        subtitle={t(
          "compliance.cchi_subtitle",
          "KSA CCHI mandate: every active Saudi employee + dependents must have active medical coverage. Mode-invariant per Commandment 45.",
        )}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border-subtle bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-text-tertiary">
            {t("compliance.cchi_gap_count", "Coverage gaps")}
          </div>
          <div className="mt-1 text-3xl font-semibold text-rose-700">
            {scan.data?.total_gaps ?? "—"}
          </div>
          <div className="mt-1 text-xs text-text-tertiary">
            {t("compliance.cchi_today_label", "Scan date")}:{" "}
            {scan.data?.today ?? ""}
          </div>
        </div>
        <div className="rounded-2xl border border-border-subtle bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-text-tertiary">
            {t("compliance.cchi_active_exceptions", "Active exceptions")}
          </div>
          <div className="mt-1 text-3xl font-semibold text-amber-700">
            {statusTab === "active"
              ? exceptions.data?.length ?? "—"
              : "—"}
          </div>
          <div className="mt-1 text-xs text-text-tertiary">
            {t(
              "compliance.cchi_exceptions_note",
              "HR-Director only; audit-logged.",
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-border-subtle bg-white p-4 flex flex-col">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            {t("compliance.cchi_mandate_label", "CCHI mandate")}
          </div>
          <div className="mt-1 text-sm text-text-primary">
            {t(
              "compliance.cchi_mandate_text",
              "Mutaba'aa enforces continuously. Scanner runs daily; on-demand scan refreshes the gap list here.",
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text-primary">
            {t("compliance.cchi_gaps_title", "Coverage gaps")}
          </h2>
          <button
            type="button"
            onClick={() => scan.refetch()}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            {t("compliance.cchi_refresh", "Refresh scan")}
          </button>
        </div>
        {scan.isLoading && <Loading />}
        {scan.isError && (
          <ErrorPanel message={(scan.error as Error).message} />
        )}
        {scan.data && scan.data.items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/40 p-4 text-sm text-emerald-800">
            <CheckCircle2 className="mr-2 inline h-4 w-4" />
            {t(
              "compliance.cchi_no_gaps",
              "Every active Saudi employee is covered or exempted. CCHI mandate satisfied.",
            )}
          </div>
        )}
        {scan.data && scan.data.items.length > 0 && (
          <div className="space-y-2">
            {scan.data.items.map((it) => (
              <GapItemRow
                key={it.employee_id}
                item={it}
                onGrant={() => {
                  setPrefillEmpId(it.employee_id);
                  setGrantOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text-primary">
            {t("compliance.cchi_exceptions_title", "Exceptions")}
          </h2>
          <button
            type="button"
            onClick={() => {
              setPrefillEmpId(undefined);
              setGrantOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <AlertTriangle className="h-4 w-4" />
            {t("compliance.cchi_grant_btn", "Grant exception")}
          </button>
        </div>
        <div className="mb-2 flex gap-1 border-b border-border-subtle">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusTab(s)}
              className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs capitalize ${
                statusTab === s
                  ? "border-brand-blue text-brand-deep"
                  : "border-transparent text-text-secondary hover:text-text-primary"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        {exceptions.isLoading && <Loading />}
        {exceptions.isError && (
          <ErrorPanel message={(exceptions.error as Error).message} />
        )}
        {exceptions.data && exceptions.data.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "compliance.cchi_no_exceptions",
              "No exceptions in this status.",
            )}
          </div>
        )}
        {exceptions.data && exceptions.data.length > 0 && (
          <div className="space-y-2">
            {exceptions.data.map((exc) => (
              <ExceptionRow
                key={exc.id}
                exc={exc}
                onRevoke={(id) => revoke.mutate(id)}
              />
            ))}
          </div>
        )}
      </div>

      {grantOpen && legalEntityId && (
        <GrantExceptionDialog
          legalEntityId={legalEntityId}
          prefillEmployeeId={prefillEmpId}
          onClose={() => setGrantOpen(false)}
          onGranted={() => {
            qc.invalidateQueries({ queryKey: ["cchi-exceptions"] });
            qc.invalidateQueries({ queryKey: ["cchi-scan"] });
          }}
        />
      )}
    </div>
  );
}
